import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FileText, RefreshCw, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

export default function SummaryTab({ projectId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { getAuthHeaders, API } = useAuth();

  useEffect(() => {
    fetchSummary();
  }, [projectId]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/summary/${projectId}`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/summary/${projectId}/refresh`, {}, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to refresh summary:', error);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!summary || !summary.summary) {
    return (
      <div className="card text-center py-20">
        <FileText className="mx-auto text-slate-300 mb-4" size={64} />
        <h3 className="text-xl font-medium text-slate-900 mb-2">No summary available</h3>
        <p className="text-slate-600 mb-6">Add conversations to generate an AI-powered project summary</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Project Summary</h2>
          <p className="text-sm text-slate-600">AI-generated executive summary of all research</p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-secondary flex items-center gap-2"
          data-testid="refresh-summary-button"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Generating...' : 'Refresh Summary'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Summary */}
        <div className="md:col-span-2 card p-8">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="text-blue-600" size={24} />
            <h3 className="text-xl font-heading font-semibold text-slate-900">Executive Summary</h3>
          </div>
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{summary.summary}</p>
          </div>
        </div>

        {/* Key Findings */}
        <div className="card p-8">
          <div className="flex items-center gap-2 mb-6">
            <CheckCircle className="text-green-600" size={24} />
            <h3 className="text-xl font-heading font-semibold text-slate-900">Key Findings</h3>
          </div>
          <div className="space-y-4">
            {summary.key_findings && summary.key_findings.length > 0 ? (
              summary.key_findings.map((finding, index) => (
                <div key={index} className="flex items-start gap-3" data-testid="key-finding-item">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-mono font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{finding}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No key findings available</p>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-6 card p-4 bg-slate-50">
        <p className="text-xs text-slate-500 font-mono">
          Generated by Claude Sonnet 4.5 on {new Date(summary.generated_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
